import React from "react";

// Format an inferred occupancy percentage. The leading `~` flags that the value
// is inferred from booking signals rather than measured directly — never render
// CI occupancy without it. Use 1 decimal so small competitive deltas survive
// rounding (e.g. ~63.6% vs ~64.2%).
export const fmtOcc = (v) => (v == null ? "—" : `~${Number(v).toFixed(1)}%`);

export const INFERRED_OCCUPANCY_TOOLTIP = "Estimated from booking platform inventory data";

// JSX wrapper that renders the inferred occupancy value with the spec-required
// hover tooltip. Use this in JSX call sites; reach for fmtOcc only when you
// need the raw string (e.g. for an aria-label or chart axis tick).
export const InferredOccupancy = ({ value, className = "" }) => {
  if (value == null) return <span className={className}>—</span>;
  return (
    <span className={`cursor-help ${className}`.trim()} title={INFERRED_OCCUPANCY_TOOLTIP}>
      {fmtOcc(value)}
    </span>
  );
};

// Display labels for CI category codes. The backend sends lowercase codes
// (`rv`, `lodging`, `tent`, `glamping`) but the UI/spec uses Title Case with
// "RV" capitalized. Unknown codes fall back to capitalizing the first letter
// so a new backend code still renders something readable.
export const CATEGORY_LABELS = {
  rv: "RV",
  lodging: "Lodging",
  tent: "Tent",
  glamping: "Glamping",
};

export const categoryLabel = (code) => {
  if (!code) return "—";
  const norm = String(code).toLowerCase().trim();
  return CATEGORY_LABELS[norm] ?? (code.charAt(0).toUpperCase() + code.slice(1));
};

// Title-case a competitor name. Atlas ingestion sometimes delivers ALL CAPS
// names (e.g. "HAWKS HIDEAWAY CAMPING, LLC"); this frontend patch normalizes
// those by lowercasing the string, then capitalizing the first letter of each
// word. Punctuation (commas, periods, ampersands) is preserved because we only
// re-case the first character of each space-separated token (to-do B6).
//
// Names that already contain lowercase letters are returned UNCHANGED — that
// protects intended casing such as acronyms ("RV", "LLC") and normal-cased
// names, so the patch only touches the ALL CAPS ingestion artifacts. A backend
// fix would normalize names at scrape/sync time; this is a targeted UI patch.
export const titleCaseName = (name) => {
  if (name == null) return name;
  const str = String(name);
  if (!str.trim()) return str;
  // Only re-case names with no lowercase letters (effectively ALL CAPS). A
  // mixed-case name already carries intended casing — leave it alone.
  if (/[a-z]/.test(str)) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ")
    .trim();
};
